import React from "react"

const LOCAL_STORAGE_KEY = "default-theme"

export enum Theme {
  Dark = "dark",
  Light = "light",
}

function themeFromString(s?: string | null): Theme | null {
  if (s === Theme.Dark || s === Theme.Light) {
    return s
  }
  return null
}

function getTheme(): Theme {
  const isBrowserDefaultDark = () =>
    window.matchMedia("(prefers-color-scheme: dark)").matches
  const localStorageTheme = themeFromString(
    localStorage.getItem(LOCAL_STORAGE_KEY),
  )
  const browserDefault = isBrowserDefaultDark() ? Theme.Dark : Theme.Light
  return localStorageTheme || browserDefault
}

function storeTheme(theme: Theme) {
  localStorage.setItem(LOCAL_STORAGE_KEY, theme)
  // Dispatch custom event to notify all components
  window.dispatchEvent(new CustomEvent("themeChange", { detail: theme }))
}

function assignTheme(theme: Theme) {
  const htmlTag = document.getElementsByTagName("html")[0]
  if (htmlTag) {
    if (theme === "dark") {
      htmlTag.classList.add("dark")
    } else {
      htmlTag.classList.remove("dark")
    }
  }
}

export function useTheme() {
  const [theme, setTheme] = React.useState(getTheme())

  React.useEffect(() => {
    const handleStorageChange = () => {
      const newTheme = getTheme()
      setTheme(newTheme)
      assignTheme(newTheme)
    }

    const handleThemeChange = (event: CustomEvent) => {
      setTheme(event.detail)
      assignTheme(event.detail)
    }

    // Listen for storage changes from other tabs/windows
    window.addEventListener("storage", handleStorageChange)

    // Listen for custom theme change events
    window.addEventListener("themeChange", handleThemeChange as EventListener)

    // Listen for system theme changes
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)")
    const handleMediaChange = () => {
      const newTheme = getTheme()
      setTheme(newTheme)
      assignTheme(newTheme)
    }

    mediaQuery.addEventListener("change", handleMediaChange)

    return () => {
      window.removeEventListener("storage", handleStorageChange)
      window.removeEventListener(
        "themeChange",
        handleThemeChange as EventListener,
      )
      mediaQuery.removeEventListener("change", handleMediaChange)
    }
  }, [])

  const toggleTheme = () => {
    const currentTheme = getTheme()
    const nextTheme = currentTheme === Theme.Dark ? Theme.Light : Theme.Dark
    setTheme(nextTheme)
    storeTheme(nextTheme)
    assignTheme(nextTheme)
    return nextTheme
  }

  React.useEffect(() => {
    assignTheme(theme)
  }, [theme])

  return {
    theme,
    toggleTheme,
  }
}
